/**
 * 线上错误上报（W7）。
 *
 * ⚠⚠ **接它的全部意义是能和后端那一半对上**（计划 §10.4 的判据原话）：
 *   「制造一个 SSE 502，Sentry 里那条**能看到 traceId**，且拿它**能在后端 OTel 里
 *   查到同一条链路**。只是「Sentry 收到了报错」不算做完。」
 *   所以 `traceId` 是打成 **tag**（Sentry 里 tag 才可检索），不是塞进 extra ——
 *   塞 extra 就只能一条条点开看，对不上后端那一半。
 *
 * ⚠ **没配 DSN 就整体 no-op**：dev 和 e2e 都不该往外发东西。判据是 DSN 而不是
 *   `import.meta.env.PROD` —— 预发/本地想临时开一下，配上 DSN 就行，不用改代码。
 *
 * ⚠ DSN **在函数里读**、不在模块顶层读：顶层读会把值烧死在模块加载那一刻，
 *   单测就得靠 `resetModules` + 动态 import 才能换环境，没必要。
 */
import * as Sentry from '@sentry/react'
import { isApiError } from '@cione/api-client'
import { needsLogin } from './auth-errors'

/** Sanitize final SDK payloads too: history breadcrumbs and request.url bypass API reporting. */
export function sanitizeMonitoringData<T>(value: T): T {
  const visit = (item: unknown, key = ''): unknown => {
    if (/^(inviteToken|speechAccessToken|mediaToken)$/i.test(key)) return '[redacted]'
    if (typeof item === 'string') {
      return item
        .replace(/(\/meeting\/(?:join|guest)\/)[^\s/?#"'<>]+/gi, '$1[redacted]')
        .replace(/(\/meeting\/)[^\s?#"'<>]+/gi, '$1[redacted]')
        .replace(/cione\.meeting\.[A-Za-z0-9_.-]+/g, 'cione.meeting.[redacted]')
        .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[redacted]')
    }
    if (Array.isArray(item)) return item.map((entry: unknown) => visit(entry))
    if (item && typeof item === 'object')
      return Object.fromEntries(
        Object.entries(item).map(([name, entry]) => [name, visit(entry, name)]),
      )
    return item
  }
  return visit(value) as T
}

function dsn(): string {
  return (import.meta.env.VITE_SENTRY_DSN as string | undefined) ?? ''
}

/**
 * 这些码**不上报** —— 它们是正常路径，不是故障：
 *   - `CLIENT.ABORTED`：用户自己点了「停止」；
 *   - `needsLogin()` 那一族：登录前置（方案 A）下"这一步要先登录"是设计如此，
 *     上报只会把 Sentry 灌满噪音，真故障反而被淹掉。
 * ⚠ 判"要不要登录"复用 `auth-errors` 那张表，不在这里另写一份码清单 —— 两份必然漂。
 */
function worthReporting(code: string): boolean {
  if (code === 'CLIENT.ABORTED') return false
  return !needsLogin(code)
}

export function initMonitoring(): void {
  const url = dsn()
  if (!url) return
  Sentry.init({
    dsn: url,
    environment: import.meta.env.MODE,
    beforeSend: (event) => sanitizeMonitoringData(event),
    beforeBreadcrumb: (breadcrumb) => sanitizeMonitoringData(breadcrumb),
    /*
      ⚠⚠ **`integrations` 是与默认集成「合并」，不是「替换」**（v8+ 起如此；同名的会覆盖
        默认那一个）。所以写 `integrations: []` **什么都关不掉** ——
        2026-09-03 在跑着的 app 里实测：那样写完仍有 11 个默认集成在跑
        （Breadcrumbs / BrowserApiErrors / BrowserSession / GlobalHandlers / …）。
        真要清空得用 `defaultIntegrations: false` 或函数形式 `(defaults) => []`，
        但**不该那么做** —— GlobalHandlers（接未捕获异常）、LinkedErrors、Dedupe、
        InboundFilters 正是这个功能的价值所在，清了等于装了个不工作的上报。
      ✅ tracing / replay **本来就不在默认集成里**，不加就是没有（实测 `hasTracing`
        与 `hasReplay` 都是 false，两个 sampleRate 也都没设）。回放尤其不能开：
        它会把**对话内容**整段录进去，那要先过隐私评审，是另一件事。
      ⚠⚠ 唯一需要**主动收窄**的默认集成是 Breadcrumbs：它默认记录 fetch/xhr 的
        **URL** 和 console 输出 —— `/api/travelers?keyword=张三` 会以面包屑形式带出姓名，
        而这一条绕过了下面 `dataCollection.urlQueryParams` 的口子（那管的是事件里的
        请求上下文，不是面包屑）。同名覆盖掉默认那个实例，关掉这三样。
      ⚠ `dom` 与 `history` 留着：点了哪个按钮、跳了哪个路由，对排查有用且不含明文。
    */
    integrations: [Sentry.breadcrumbsIntegration({ fetch: false, xhr: false, console: false })],
    /*
      ⚠⚠⚠ **这一整块是必须的，不是保守**。SDK 的默认值（`@sentry/core` 的
        `types/datacollection.d.ts`）是**尽量多收**，而这个应用手里有明文证件号 ——
        照默认上线等于把它发给一个第三方 SaaS，直接违反仓库里到处写着的那条纪律
        （`travelers.plaintextHint`：证件号仅用于出票，不会被展示或缓存；
         后端连掩码回传都拒）。
      ⚠ 关掉这些**不影响** §10.4 的判据：与后端 OTel 对账靠的是我们自己打的
        `trace_id` tag，不靠 SDK 采集的请求头。真正的上下文在后端那一半，
        拿 traceId 过去查就是了 —— 这正是当初选这个判据的用意。
    */
    dataCollection: {
      // 默认就是 false，显式写死 —— 默认值会变，纪律不该跟着变
      userInfo: false,
      // ⚠⚠ 默认是**四种 body 全收**。出行人新建/编辑的请求体里有明文 `docNo`
      httpBodies: [],
      // ⚠ 默认 true。`/api/travelers?keyword=张三` —— 姓名会进 URL
      urlQueryParams: false,
      // ⚠⚠ 默认 true，原文是 "capture local variable values in stack frames"。
      //   表单提交那条栈上的局部变量就握着证件号
      stackFrameVariables: false,
      // ⚠ 请求头里有 Authorization bearer。SDK 声称会滤敏感值，但没必要赌
      httpHeaders: { request: false, response: false },
      // 令牌存 localStorage 不存 cookie，所以这条本来就没东西可漏 —— 白给的就关掉
      cookies: false,
    },
  })
}

/**
 * 上报一个出网错误，带上能和后端对上的 `traceId`。
 *
 * @param where 出错的地方（如 `chat.stream`）—— 进 tag，便于按位置分组。
 */
export function reportApiError(error: unknown, where: string): void {
  if (!dsn()) return

  if (isApiError(error)) {
    if (!worthReporting(error.code)) return
    Sentry.withScope((scope) => {
      // ⚠⚠ traceId 必须是 tag：Sentry 里只有 tag 可检索，这条正是与后端 OTel 对账的钩子
      scope.setTag('trace_id', error.traceId || '(none)')
      scope.setTag('api_code', error.code)
      scope.setTag('where', where)
      if (error.httpStatus !== undefined) scope.setTag('http_status', String(error.httpStatus))
      Sentry.captureException(error)
    })
    return
  }

  // 不是 ApiError（脚本报错、解析炸了…）：没有 traceId 可带，但更该上报
  Sentry.withScope((scope) => {
    scope.setTag('where', where)
    Sentry.captureException(error)
  })
}
